import React from "react";
import { Box } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import Flx from "../../../components/layout/Flx";
import T from "../submissionTableTokens";
import { LANES, loanDrilldownHref, submissionIsAssigned } from "../submissionHelpers";

export const Dash = () => (
  <Box component="span" sx={{ color: T.ink300 }}>
    —
  </Box>
);

// Same values as the lanes — a row's `__type` IS its lane, so they must not
// drift apart.
export const SUBMISSION_TYPES = {
  LOAN_APPLICATION: LANES.LOAN_APPLICATION,
  CREDIT_AUTHORIZATION: LANES.CREDIT_AUTHORIZATION,
};

export const TYPE_META = {
  [SUBMISSION_TYPES.LOAN_APPLICATION]: {
    label: "Application",
    long: "Loan application",
    bg: "#EEF3FE",
    fg: "#1D49C4",
  },
  [SUBMISSION_TYPES.CREDIT_AUTHORIZATION]: {
    label: "Credit auth",
    long: "Credit authorization",
    bg: "#E7F4F9",
    fg: "#0E6E8C",
  },
};

/** The submission kind, spelled out — used wherever a badge would be redundant. */
export const submissionTypeLong = (row = {}) =>
  TYPE_META[row.__type]?.long || "Submission";

export const TypeBadge = ({ type }) => {
  const m = TYPE_META[type];
  if (!m) return <Dash />;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: 650,
        lineHeight: 1.5,
        color: m.fg,
        background: m.bg,
        borderRadius: "6px",
        px: "8px",
        py: "4px",
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </Box>
  );
};

/**
 * The status pill both states share. A filled chip rather than bare text — each
 * one is pressable, and the fill is what says so at a glance down the column.
 */
export const Tag = ({ bg, fg, children, icon, trailingIcon, sx, ...rest }) => (
  <Box
    component="span"
    {...rest}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      fontFamily: "inherit",
      fontSize: "11.5px",
      fontWeight: 700,
      letterSpacing: ".02em",
      lineHeight: 1.5,
      color: fg,
      background: bg,
      borderRadius: "6px",
      px: "8px",
      py: "3px",
      whiteSpace: "nowrap",
      textDecoration: "none",
      ...sx,
    }}
  >
    {icon}
    {children}
    {trailingIcon}
  </Box>
);

/**
 * The three action chips, shared by the Status column and the expanded panel so
 * the same action never appears as two different-looking controls.
 *
 * `stopPropagation` on all of them: in the grid they sit inside a row whose
 * click expands it.
 */
export const AssignToLoanChip = ({ onClick }) => (
  <Tag
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    bg="#fef3e2"
    fg={T.amber}
    trailingIcon={
      // Negative margins keep the pill the same footprint as the green one.
      <ArrowDropDownRoundedIcon
        sx={{ fontSize: 16, ml: "-3px", mr: "-4px", flexShrink: 0 }}
      />
    }
    sx={{
      border: "1px solid transparent",
      cursor: "pointer",
      transition: "background .12s, border-color .12s",
      "&:hover": { background: "#fde8c8", borderColor: T.amber },
    }}
  >
    Assign To Loan
  </Tag>
);

/** The quiet chip: a bordered white pill. Create Loan and Download share it. */
export const OutlineActionChip = ({ icon, children, onClick }) => (
  <Tag
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    bg="#fff"
    fg={T.ink500}
    icon={icon}
    sx={{
      border: `1px solid ${T.line}`,
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      "&:hover": { background: T.bg, borderColor: T.ink300, color: T.ink900 },
    }}
  >
    {children}
  </Tag>
);

export const CreateLoanChip = ({ onClick }) => (
  <OutlineActionChip
    icon={<AddRoundedIcon sx={{ fontSize: 13 }} />}
    onClick={onClick}
  >
    Create Loan
  </OutlineActionChip>
);

/** The filled counterpart — brand blue, for an action that isn't optional. */
export const ContainedActionChip = ({ icon, children, onClick }) => (
  <Tag
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    fg="#fff"
    icon={icon}
    sx={{
      border: "1px solid transparent",
      cursor: "pointer",
      background: (t) => t.palette.primary.main,
      transition: "background .12s",
      "&:hover": { background: (t) => t.palette.primary.dark },
    }}
  >
    {children}
  </Tag>
);

/**
 * Whether "Create Loan" can be offered for this submission.
 *
 * LOAN APPLICATIONS ONLY. create_deal_from_submission builds the new loan's
 * `subjectProperties` from `sp_array`, which it only assigns in the
 * non-credit-auth branch — so calling it for a credit auth raises
 * UnboundLocalError and 500s. Offering the button there would be offering a
 * guaranteed failure. (underwriting.py create_deal_from_submission.)
 */
export const canCreateLoanFrom = (row) =>
  row?.__type === LANES.LOAN_APPLICATION;

/**
 * Status, and the way into changing it.
 *
 * - Needs a loan → a button that opens the assign dialog. The column that says a
 *   submission isn't on a loan is the obvious place to put it on one, and the
 *   caret marks it as pressable.
 * - On a loan → a link to that loan's drilldown, opened in a NEW TAB. This queue
 *   is a worklist you come back to; navigating away to glance at a loan loses
 *   your place. A real `<a href>`, so middle-click and cmd-click behave.
 *
 * Both `stopPropagation` — they sit inside a grid row whose click expands it.
 */
/**
 * The loan a submission is on, as a link to that loan's drilldown opened in a
 * NEW TAB. This queue is a worklist you come back to; navigating away to glance
 * at a loan loses your place. A real `<a href>`, so middle-click and cmd-click
 * behave.
 *
 * Shared by the Status column and the expanded panel, so "on a loan" looks the
 * same wherever it's reported.
 */
export const LoanLinkChip = ({ row }) => {
  const label =
    row?.loanNumber != null ? `Loan #${row.loanNumber}` : "On a loan";
  const href = loanDrilldownHref(row?.dealId);
  const shared = {
    bg: "#effaf2",
    fg: T.green,
    icon: <CheckCircleRoundedIcon sx={{ fontSize: 13 }} />,
  };

  // Assigned but no dealId to point at — still a status, just not a link.
  if (!href) return <Tag {...shared}>{label}</Tag>;

  return (
    <Tag
      {...shared}
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      trailingIcon={
        <OpenInNewRoundedIcon
          sx={{ fontSize: 12, ml: "1px", flexShrink: 0, opacity: 0.75 }}
        />
      }
      sx={{
        border: "1px solid transparent",
        cursor: "pointer",
        transition: "background .12s, border-color .12s",
        "&:hover": { background: "#ddf3e4", borderColor: T.green },
      }}
    >
      {label}
    </Tag>
  );
};

/**
 * Status, and the way into changing it.
 *
 * - Needs a loan → a button that opens the assign dialog. The column that says a
 *   submission isn't on a loan is the obvious place to put it on one, and the
 *   caret marks it as pressable.
 * - On a loan → LoanLinkChip, above.
 *
 * Both `stopPropagation` — they sit inside a grid row whose click expands it.
 */
export const StatusCell = ({ row, onAssign, onCreateLoan }) => {
  if (submissionIsAssigned(row)) return <LoanLinkChip row={row} />;

  if (!onAssign) {
    return (
      <Tag bg="#fef3e2" fg={T.amber}>
        Needs a loan
      </Tag>
    );
  }

  return (
    <Flx centerY wrap gap={0.625} sx={{ minWidth: 0 }}>
      <AssignToLoanChip onClick={() => onAssign(row)} />
      {/* The quieter of the two: attaching to an existing loan is the common
          case, and creating one is the escape hatch when none exists. */}
      {onCreateLoan && canCreateLoanFrom(row) && (
        <CreateLoanChip onClick={() => onCreateLoan(row)} />
      )}
    </Flx>
  );
};
